/**
 * Validators here determine whether or not to show an error
 * i.e. a return value of true means the value is invalid.
 */

import get from 'lodash/get';
import has from 'lodash/has';
import trimEnd from 'lodash/trimEnd';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import basicInfoMessages from './messages';
import admissionMessages from './admissions/messages';
import overviewMessages from './overview/messages';
import studentLifeMessages from './studentLife/messages';
import studiesMessages from './studies/messages';
import costMessages from './costs/messages';
import { testScoresLabels } from './overview/test-scores';
import { isUrlValid } from '../../../utils/url';

export const gpaPattern = /^(4\.0|[1-3]\.[0-9]|0\.[1-9])$/;
const gpaValidator = (value, basicInfo, key) => {
  if (isEmpty(value)) return false;
  if (!gpaPattern.test(value)) return true;

  const gpa = get(basicInfo, 'overview.gpa', {});
  switch (key) {
    case '25':
      return Number(value) > Number(get(gpa, '50.value', 0) || value);
    case '50':
      return Number(value) > Number(get(gpa, '75.value', 0) || value);
    case '75':
      return Number(value) < Number(get(gpa, '50.value', 0) || value);
  }
  /* istanbul ignore next */
  return false;
};

export const positiveNumberValidator = (value) => {
  if (isEmpty(value)) return false;
  return !value.match(/^(0|[1-9]+[0-9]*)$/);
};

export const receivingAidValidator = (value) => {
  if (isEmpty(value)) return false;
  return ((value.length > 2 && value !== '100') || !(/^\d+$/.test(value)));
};

const isNumber = (value) => {
  /* istanbul ignore if */
  if (isEmpty(value)) return false;
  return (!!value && !value.match(/^\d+$/));
};

const lengthValidator = (value, number) => {
  if (isEmpty(value)) return false;
  return !!value && (value.length > number || !value.match(/^\d+$/));
};

/* eslint-disable */
const emailPat = "(([^<>\(\)[\\]\\.,;:\\s@\\\"]+(\\.[^<>\(\)[\\]\\.,;:\\s@\\\"]+)*)|(\\\".+\\\"))@((\\[[0-9]{1,3}"
+ "\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))";
const phonePattern = "^([\+])?[\(\)\\-0-9a-zA-Z\. ]*$";
/* eslint-enable */
const emailRegExp = new RegExp(emailPat);
const emailValidator = (value) => {
  if (value === null) return false;
  if (value.length === 0) return false;

  return !(emailRegExp.test(value));
};

const phoneRegex = new RegExp(phonePattern);
const phoneValidator = (value) => !phoneRegex.test(value);
const zipValidator = (value) => !(/^[a-zA-Z0-9\- ,]*$/.test(value));
const linkValidator = (value) => {
  if (value === null) return false;
  return !isUrlValid(value);
};

const contactValidationGroup = {
  email: emailValidator,
  phoneNumber: phoneValidator,
  tollFreeNumber: phoneValidator,
  fax: phoneValidator,
  zip: zipValidator,
};

export const validators = {
  overview: {
    gpa: {
      25: (value, basicInfo) => gpaValidator(value, basicInfo, '25'),
      50: (value, basicInfo) => gpaValidator(value, basicInfo, '50'),
      75: (value, basicInfo) => gpaValidator(value, basicInfo, '75'),
    },
    admissionsOffice: contactValidationGroup,
    applicationMailingAddress: contactValidationGroup,
    financialAid: contactValidationGroup,
    testScores: {
      25: positiveNumberValidator,
      50: positiveNumberValidator,
      75: positiveNumberValidator,
    },
    studentFacultyRatio: positiveNumberValidator,
  },
  admissions: {
    acceptanceRate: {
      applicationsAccepted: (value) => lengthValidator(value, 6),
      applicationsReceived: (value) => lengthValidator(value, 6),
    },
    fees: {
      applicationFee: (value) => lengthValidator(value, 6),
      deposit: (value) => lengthValidator(value, 6),
      internationalApplicationFee: (value) => lengthValidator(value, 6),
      internationalDepositFee: (value) => lengthValidator(value, 6),
      transferApplicationFee: (value) => lengthValidator(value, 6),
      transferDepositFee: (value) => lengthValidator(value, 6),
    },
    recommendedCourses: {
      requiredYears: positiveNumberValidator,
      recomendationYears: positiveNumberValidator,
    },
    applicationFactors: {
      importance: isEmpty,
    },
  },
  studies: {
    studentRetention: positiveNumberValidator,
    graduationRate4: positiveNumberValidator,
    graduationRate6: positiveNumberValidator,
  },
  studentLife: {
    schoolSize: {
      graduateMen: (value) => lengthValidator(value, 9) || positiveNumberValidator(value),
      graduateWomen: (value) => lengthValidator(value, 9) || positiveNumberValidator(value),
      undergraduateMen: (value) => lengthValidator(value, 9) || positiveNumberValidator(value),
      undergraduateWomen: (value) => lengthValidator(value, 9) || positiveNumberValidator(value),
    },
    ethnicity: {
      africanAmerican: positiveNumberValidator,
      hispanic: positiveNumberValidator,
      nativePacificIslander: positiveNumberValidator,
      twoOrMoreRaces: positiveNumberValidator,
      caucasian: positiveNumberValidator,
      asian: positiveNumberValidator,
      nativeAmerican: positiveNumberValidator,
      nativeUnknown: positiveNumberValidator,
      nonResidentAlien: positiveNumberValidator,
      international: positiveNumberValidator,
    },
    ageData: positiveNumberValidator,
    computingResources: {
      numberOfMacsInComputerCenters: positiveNumberValidator,
      numberOfPCsInComputerCenters: positiveNumberValidator,
      numberOfMacsInDepartmentLabs: positiveNumberValidator,
      numberOfPCsInDepartmentLabs: positiveNumberValidator,
      numberOfMacsInLibraries: positiveNumberValidator,
      numberOfPCsInLibraries: positiveNumberValidator,
      numberOfMacsInStudentCenters: positiveNumberValidator,
      numberOfPCsInStudentCenters: positiveNumberValidator,
      numberOfMacsInResidences: positiveNumberValidator,
      numberOfPCsInResidences: positiveNumberValidator,
      numberOfMacsInOther: positiveNumberValidator,
      numberOfPCsInOther: positiveNumberValidator,
    },
  },
  costs: {
    averageNetPrices: {
      '0-30': (value) => lengthValidator(value, 6),
      '30-48': (value) => lengthValidator(value, 5),
      '48-75': (value) => lengthValidator(value, 5),
      '75-110': (value) => lengthValidator(value, 5),
      '110+': isNumber,
    },
    receivingAid: {
      grant: receivingAidValidator,
      pellGrant: receivingAidValidator,
      studentLoan: receivingAidValidator,
    },
    averageAmountOfAid: {
      grant: (value) => lengthValidator(value, 6),
      pellGrant: (value) => lengthValidator(value, 6),
      studentLoan: (value) => lengthValidator(value, 6),
    },
  },
  transferInfo: {
    website: linkValidator,
    supportWebsite: linkValidator,
    creditTransferInfoWebsite: linkValidator,
    financialAidInfoWebsite: linkValidator,
    transferAdmissionsContact: {
      url: linkValidator,
    },
  },
};

export const findValidator = (path) => get(validators, path);

const getMessagePathFromKeys = (keyPath, data, dataKey) => {
  let messagePath = [];
  const isTestScoresPath = keyPath.includes('.testScores');
  if (isTestScoresPath) {
    const keys = keyPath.split('.testScores');
    messagePath = [
      basicInfoMessages[keys[0]],
      basicInfoMessages.testScores,
      data.type,
      testScoresLabels[dataKey],
    ];
  } else {
    const keys = keyPath.split('.');
    keys.forEach((key) => {
      const msg = basicInfoMessages[key] || admissionMessages[key] || overviewMessages[key] ||
        studentLifeMessages[key] || studiesMessages[key] || costMessages[key];
      /* istanbul ignore else */
      if (msg) {
        messagePath.push(msg);
      }
    });
  }
  return messagePath;
};

export const getValidationErrors = (data, path = '') => {
  let errors = [];

  if (!isObject(data)) {
    return errors;
  }

  Object.keys(data).forEach((key) => {
    const localPath = path.length ? `${path}.${key}` : key;
    const item = data[key];

    /* istanbul ignore else */
    if (isObject(item)) {
      if (has(item, 'value')) {
        const validator = get(validators, localPath);

        if (validator && validator(item.value)) {
          errors.push({ label: item.label || `${data.type}-${key}`, errorPath: getMessagePathFromKeys(localPath, data, key) });
        }
      } else {
        const childErrors = getValidationErrors(item, localPath);
        if (childErrors.length > 0) {
          errors = errors.concat(childErrors);
        }
      }
    } else if ((typeof item === 'string' || typeof item === 'number') && (key !== 'eid' && key !== 'type')) {
      const validatorPath = `${trimEnd(path, '1234567890.')}.${key}`;
      const validator = findValidator(validatorPath);
      if (validator && validator(item)) {
        errors.push({ label: item.label|| `${data.type}-${key}`, errorPath: getMessagePathFromKeys(localPath, data, key) });
      }
    }
  });

  return errors;
};

export default function decorateFieldWithValidation(path, data, basicInfo) {
  const dataCopy = { ...data };

  /* istanbul ignore else */
  if (isObject(dataCopy)) {
    if ('value' in dataCopy) {
      const validator = findValidator(path);
      if (validator) {
        dataCopy.showError = validator(dataCopy.value, basicInfo);
      }
    } else {
      Object.keys(dataCopy).forEach((key) => {
        const field = dataCopy[key];
        const validator = findValidator(`${path}.${key}`);

        if (validator) {
          if (typeof field === 'string' && (key !== 'eid' && key !== 'type')) {
            if (!dataCopy.showError) dataCopy.showError = [];
            dataCopy.showError[key] = validator(field);
          } else /* istanbul ignore else */ if (field && 'value' in field) {
            dataCopy[key].showError = validator(field.value);
          }
        }
      });
    }
  }
  return dataCopy;
}
